// Color interpolator functions from d3-scale-chromatic (not d3-interpolate).
// Each maps a value in [0,1] to an RGB color string for sequential scales.
import {
  interpolateBlues,
  interpolateGreens,
  interpolateOranges,
  interpolatePurples,
  interpolateReds,
} from 'd3-scale-chromatic';

// ---------------------------------------------------------------------------
// Color palettes
// ---------------------------------------------------------------------------

export const DEFAULT_FLOW_COLORS = [
  '#2563eb',
  '#10b981',
  '#f59e0b',
  '#ef4444',
  '#8b5cf6',
  '#ec4899',
];

// Neutral/muted so a routed path reads as its own layer rather than
// competing with FlowLayer's categorical arc colors or vessel status dots.
export const DEFAULT_ROUTE_COLOR = '#64748b';

export const VESSEL_STATUS_COLORS: Record<string, string> = {
  'under way': '#10b981',
  'under way using engine': '#10b981',
  'under way sailing': '#10b981',
  'at anchor': '#f59e0b',
  moored: '#6b7280',
  default: '#6b7280',
};

// ---------------------------------------------------------------------------
// Cartographic base colors — warm palette that reads as a map, not a data grid.
// Light theme uses sandy land on blue ocean; dark theme inverts to muted tones.
// CSS custom properties aren't accessible inside SVG fill attributes at render
// time, so we hardcode both palettes and select at render via a prefers check.
// ---------------------------------------------------------------------------

export const MAP_COLORS = {
  light: { ocean: '#c9dce8', land: '#e8dcc8', border: '#b0a898' },
  dark: { ocean: '#1a2533', land: '#2a2520', border: '#4a4540' },
};

// Maps the colorScheme prop enum to d3-interpolate functions for choropleth
export const COLOR_INTERPOLATORS: Record<string, (t: number) => string> = {
  blues: interpolateBlues,
  reds: interpolateReds,
  greens: interpolateGreens,
  oranges: interpolateOranges,
  purples: interpolatePurples,
};

// ---------------------------------------------------------------------------
// ISO 3166-1 alpha-3 → numeric code mapping
// world-atlas uses numeric IDs as TopoJSON feature IDs. This lookup lets
// the chatbot provide the more familiar alpha-3 codes (e.g. "SAU", "USA").
// Covers all countries in the Natural Earth 110m dataset.
// ---------------------------------------------------------------------------

export const ALPHA3_TO_NUMERIC: Record<string, string> = {
  AFG: '004',
  ALB: '008',
  DZA: '012',
  AGO: '024',
  ARG: '032',
  ARM: '051',
  AUS: '036',
  AUT: '040',
  AZE: '031',
  BHS: '044',
  BGD: '050',
  BLR: '112',
  BEL: '056',
  BLZ: '084',
  BEN: '204',
  BTN: '064',
  BOL: '068',
  BIH: '070',
  BWA: '072',
  BRA: '076',
  BRN: '096',
  BGR: '100',
  BFA: '854',
  BDI: '108',
  KHM: '116',
  CMR: '120',
  CAN: '124',
  CAF: '140',
  TCD: '148',
  CHL: '152',
  CHN: '156',
  COL: '170',
  COG: '178',
  COD: '180',
  CRI: '188',
  CIV: '384',
  HRV: '191',
  CUB: '192',
  CYP: '196',
  CZE: '203',
  DNK: '208',
  DJI: '262',
  DOM: '214',
  ECU: '218',
  EGY: '818',
  SLV: '222',
  GNQ: '226',
  ERI: '232',
  EST: '233',
  SWZ: '748',
  ETH: '231',
  FLK: '238',
  FJI: '242',
  FIN: '246',
  FRA: '250',
  GAB: '266',
  GMB: '270',
  GEO: '268',
  DEU: '276',
  GHA: '288',
  GRC: '300',
  GRL: '304',
  GTM: '320',
  GIN: '324',
  GNB: '624',
  GUY: '328',
  HTI: '332',
  HND: '340',
  HUN: '348',
  ISL: '352',
  IND: '356',
  IDN: '360',
  IRN: '364',
  IRQ: '368',
  IRL: '372',
  ISR: '376',
  ITA: '380',
  JAM: '388',
  JPN: '392',
  JOR: '400',
  KAZ: '398',
  KEN: '404',
  PRK: '408',
  KOR: '410',
  XKX: '-99', // Kosovo — uses -99 in Natural Earth
  KWT: '414',
  KGZ: '417',
  LAO: '418',
  LVA: '428',
  LBN: '422',
  LSO: '426',
  LBR: '430',
  LBY: '434',
  LTU: '440',
  LUX: '442',
  MKD: '807',
  MDG: '450',
  MWI: '454',
  MYS: '458',
  MLI: '466',
  MRT: '478',
  MEX: '484',
  MDA: '498',
  MNG: '496',
  MNE: '499',
  MAR: '504',
  MOZ: '508',
  MMR: '104',
  NAM: '516',
  NPL: '524',
  NLD: '528',
  NZL: '554',
  NIC: '558',
  NER: '562',
  NGA: '566',
  NOR: '578',
  OMN: '512',
  PAK: '586',
  PAN: '591',
  PNG: '598',
  PRY: '600',
  PER: '604',
  PHL: '608',
  POL: '616',
  PRT: '620',
  PRI: '630',
  QAT: '634',
  ROU: '642',
  RUS: '643',
  RWA: '646',
  SAU: '682',
  SEN: '686',
  SRB: '688',
  SLE: '694',
  SVK: '703',
  SVN: '705',
  SLB: '090',
  SOM: '706',
  ZAF: '710',
  SSD: '728',
  ESP: '724',
  LKA: '144',
  SDN: '729',
  SUR: '740',
  SWE: '752',
  CHE: '756',
  SYR: '760',
  TWN: '158',
  TJK: '762',
  TZA: '834',
  THA: '764',
  TLS: '626',
  TGO: '768',
  TTO: '780',
  TUN: '788',
  TUR: '792',
  TKM: '795',
  UGA: '800',
  UKR: '804',
  ARE: '784',
  GBR: '826',
  USA: '840',
  URY: '858',
  UZB: '860',
  VUT: '548',
  VEN: '862',
  VNM: '704',
  PSE: '275',
  YEM: '887',
  ZMB: '894',
  ZWE: '716',
  NCL: '540',
  SOL: '090', // alias
  CYN: '196', // alias for Northern Cyprus (same feature in NE)
  ATF: '260',
};

// Reverse lookup: numeric → alpha-3 (built once at module load)
export const NUMERIC_TO_ALPHA3: Record<string, string> = {};
for (const [alpha3, numeric] of Object.entries(ALPHA3_TO_NUMERIC)) {
  // First entry wins — avoids alias overwrites
  if (!NUMERIC_TO_ALPHA3[numeric]) {
    NUMERIC_TO_ALPHA3[numeric] = alpha3;
  }
}
